import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { searchSirene } from "../infrastructure/api/sireneApi";
import { checkPrimaryRegistrationNumberExistence } from "../infrastructure/adapters/organisationService";
import { logger } from "../../../infrastructure/services/LoggerService";
export function useSireneSearch() {
    const { t } = useI18n();
    const sireneOptions = ref([]);
    const sireneTotalCount = ref(0);
    const sireneLoading = ref(false);
    const sireneUnavailable = ref(false);
    const lastSireneQuery = ref("");
    const showAllResultsModal = ref(false);
    const allSireneOptions = ref([]);
    const allSireneLoading = ref(false);
    const allSireneError = ref(false);
    const allResultsQuery = ref("");
    let sireneDebounce = null;
    function mapSireneOptions(items) {
        return items
            .filter((r) => r.legalName?.trim())
            .map((r) => ({
            value: r.legalName,
            label: r.legalName,
            description: (r.address ?? [r.postalCode, r.city].filter(Boolean).join(", ")) || undefined,
            badge: r.alreadyInCrm ? t("organisation.create.alreadyInCrm") : undefined,
            badgeTheme: r.alreadyInCrm ? "orange" : undefined,
            badgeBrandIcon: r.alreadyInCrm ? "eudonet" : undefined,
            meta: {
                siret: r.siret ?? undefined,
                address: r.address ?? [r.postalCode, r.city].filter(Boolean).join(" "),
                _sireneResult: r,
                uniqueKey: r.siret ?? `${r.siren}|${[r.address, r.postalCode, r.city].filter(Boolean).join("|")}`,
            },
        }));
    }
    async function enrichWithCrmPresence(items) {
        const itemsWithSiret = items.filter((item) => item.siret);
        if (itemsWithSiret.length === 0)
            return;
        try {
            const primaryRegistrationNumbers = itemsWithSiret.map((item) => item.siret);
            const results = await checkPrimaryRegistrationNumberExistence(primaryRegistrationNumbers);
            const existingSet = new Set(results.filter((r) => r.exists).map((r) => r.primaryRegistrationNumber));
            for (const item of itemsWithSiret) {
                if (existingSet.has(item.siret))
                    item.alreadyInCrm = true;
            }
        }
        catch (error) {
            logger.error("Primary registration number CRM presence check failed", error);
        }
    }
    function onLegalNameSearch(query) {
        if (sireneDebounce)
            clearTimeout(sireneDebounce);
        const trimmed = query.trim();
        if (trimmed.length < 3) {
            sireneOptions.value = [];
            sireneTotalCount.value = 0;
            return;
        }
        sireneDebounce = setTimeout(async () => {
            sireneLoading.value = true;
            lastSireneQuery.value = query;
            try {
                const { items, totalCount } = await searchSirene(query);
                await enrichWithCrmPresence(items);
                sireneTotalCount.value = totalCount;
                sireneOptions.value = mapSireneOptions(items);
                sireneUnavailable.value = false;
            }
            catch {
                sireneOptions.value = [];
                sireneTotalCount.value = 0;
                sireneUnavailable.value = true;
            }
            finally {
                sireneLoading.value = false;
            }
        }, 400);
    }
    async function onSeeAllSireneResults() {
        showAllResultsModal.value = true;
        allSireneLoading.value = true;
        allSireneError.value = false;
        allResultsQuery.value = lastSireneQuery.value;
        try {
            // Ask for the full fan-out the badge advertises (sireneTotalCount), capped at the
            // backend's 500 max page size. Falls back to 25 when the preview has not run yet.
            const requestedSize = Math.min(Math.max(sireneTotalCount.value, 25), 500);
            const { items } = await searchSirene(lastSireneQuery.value, requestedSize);
            await enrichWithCrmPresence(items);
            allSireneOptions.value = mapSireneOptions(items);
        }
        catch (error) {
            logger.error("Failed to load all SIRENE results", error);
            allSireneOptions.value = [];
            allSireneError.value = true;
        }
        finally {
            allSireneLoading.value = false;
        }
    }
    function reset() {
        if (sireneDebounce)
            clearTimeout(sireneDebounce);
        sireneOptions.value = [];
        sireneTotalCount.value = 0;
        sireneUnavailable.value = false;
        lastSireneQuery.value = "";
        showAllResultsModal.value = false;
        allSireneOptions.value = [];
        allSireneLoading.value = false;
        allSireneError.value = false;
        allResultsQuery.value = "";
    }
    return {
        sireneOptions,
        sireneTotalCount,
        sireneLoading,
        sireneUnavailable,
        lastSireneQuery,
        showAllResultsModal,
        allSireneOptions,
        allSireneLoading,
        allSireneError,
        allResultsQuery,
        onLegalNameSearch,
        onSeeAllSireneResults,
        reset,
    };
}
